import { ReactNode, ButtonHTMLAttributes } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'

interface BaseButtonProps {
  children: ReactNode
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  fullWidth?: boolean
  loading?: boolean
  disabled?: boolean
  className?: string
}

interface ButtonAsButtonProps extends BaseButtonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseButtonProps> {
  as?: 'button'
  href?: never
  to?: never
}

interface ButtonAsLinkProps extends BaseButtonProps {
  as: 'link'
  to: string
  href?: never
}

interface ButtonAsAnchorProps extends BaseButtonProps {
  as: 'anchor'
  href: string
  to?: never
  target?: string
  rel?: string
}

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps | ButtonAsAnchorProps

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  fullWidth = false,
  loading = false,
  disabled = false,
  className,
  as = 'button',
  ...props 
}: ButtonProps) => {
  const baseClasses = 'btn inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
  
  const variantClasses = {
    primary: 'bg-accent-800 text-white hover:bg-accent-900 focus:ring-accent-500 shadow-corporate',
    secondary: 'bg-primary-100 text-primary-800 hover:bg-primary-200 focus:ring-primary-500 border border-primary-300',
    outline: 'bg-transparent text-primary-800 hover:bg-primary-50 focus:ring-primary-500 border border-primary-400',
    ghost: 'bg-transparent text-primary-700 hover:bg-primary-100 focus:ring-primary-500'
  }
  
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  }
  
  const classes = clsx(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    fullWidth && 'w-full',
    loading && 'cursor-wait',
    className
  )

  const content = (
    <>
      {loading && (
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      )}
      {children}
    </>
  )

  if (as === 'link') {
    const linkProps = props as ButtonAsLinkProps
    return (
      <Link to={linkProps.to} className={classes}>
        {content}
      </Link>
    )
  }

  if (as === 'anchor') {
    const anchorProps = props as ButtonAsAnchorProps
    return (
      <a 
        href={anchorProps.href} 
        target={anchorProps.target}
        rel={anchorProps.rel}
        className={classes}
      >
        {content}
      </a>
    )
  }

  const buttonProps = props as ButtonAsButtonProps
  return (
    <button 
      {...buttonProps}
      className={classes} 
      disabled={disabled || loading}
    >
      {content}
    </button>
  )
}

export default Button
